/**
 * 上传作品弹窗（设计稿 235:469 空态 / 235:541 图片已上传态 / 235:145 视频tab）
 *
 * - 800x700 主面板，图片作品 / 视频作品 双 tab
 * - 「公开制作流程」开关：开启后 isPublicWorkflow=true（查看流程功能下期实现，本期仅存档）
 * - 图片作品：多图上传（最多10张）+ 设为封面 + 翻页 + 页码 + 缩略图双排
 * - 视频作品：视频文件上传 + 可选封面（默认取视频首帧）
 * - 上传双形式：本地上传 / 历史图库（HistoryAssetsPanel，设计稿 235:226）
 * - 发布优先落库（POST /api/works，媒体经 temp-upload dir=works 固定到 {user}/works/），
 *   失败兜底本地 publishWork → Bong 精选图像/视频作品展示栏
 */
import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { ChevronDown, Trash2, X } from 'lucide-react';
import { useWorksStore, type LibraryAsset, type WorkflowSnapshot } from '@/stores/worksStore';
import { API_BASE } from '@/config';
import { HistoryAssetsPanel } from './HistoryAssetsPanel';

/* ============ 常量 ============ */

/** 作品类目（最多可选 2 个） */
const CATEGORIES = ['人物', '风景', '动漫', '科幻', '动物', '建筑', '抽象', '其他'];

/** 图片作品最多张数 */
const MAX_IMAGES = 10;

/** 标题最大长度（40 个汉字） */
const MAX_TITLE = 40;

/** 提示词 / 描述最大长度（500） */
const MAX_PROMPT = 500;

/** 主面板设计尺寸（双形式浮层兜底锚点基准；面板实际宽高已加视口 clamp，见主面板注释） */
const PANEL_W = 800;
const PANEL_H = 700;

/* ============ 类型 ============ */

export interface UploadWorkModalProps {
  open: boolean;
  onClose: () => void;
  /** 画布资产历史（{id,type,src,...}），映射为历史图库数据源之一 */
  assetHistory?: any[];
  /** 公开制作流程快照源：发布时调用取当前画布全部节点/连线/分组（编辑器内发布才有，
      「查看制作流程」只读画布与复制项目共用该快照，保证与实际节点一致） */
  workflowSnapshot?: () => WorkflowSnapshot | null;
}

/** 图片 tab 中的一个图片项 */
interface ImageItem {
  id: string;
  src: string;
}

/** 上传双形式浮层触发上下文 */
type UploadTarget = 'image' | 'video' | 'videoCover' | null;

/* ============ 上传管线（与 agent/utils/streamSession.ts 同款 temp-upload 约定） ============ */

/** 上传文件到 temp-upload，返回 /assets/ 路径；失败返回 null。
    dir='works' 时后端将文件固定到 {user}/works/（发布作品媒体，不受临时文件清理影响），
    并附带登录态以便按用户归档 */
async function uploadToTemp(file: File, dir?: string): Promise<string | null> {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const qs = dir ? `?dir=${encodeURIComponent(dir)}` : '';
    const headers: Record<string, string> = {};
    const token = localStorage.getItem('bongai_token');
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`${API_BASE}/api/temp-upload${qs}`, { method: 'POST', body: formData, headers });
    if (!res.ok) return null;
    const data = await res.json();
    return data.url || null;
  } catch {
    return null;
  }
}

/** 生成短 id */
const genId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/** 抓取视频首帧，返回 jpeg dataURL（用于封面默认显示；失败返回 null） */
function grabVideoFirstFrame(src: string): Promise<string | null> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.muted = true;
    (video as any).playsInline = true;
    video.preload = 'auto';
    let settled = false;
    const finish = (v: string | null) => {
      if (settled) return;
      settled = true;
      video.removeEventListener('loadeddata', onReady);
      video.removeEventListener('error', onError);
      video.removeAttribute('src');
      resolve(v);
    };
    const onReady = () => {
      try {
        if (!video.videoWidth || !video.videoHeight) { finish(null); return; }
        /* 最长边限到 960：dataURL 会存入作品记录（localStorage persist），原分辨率帧体积过大 */
        const scale = Math.min(1, 960 / Math.max(video.videoWidth, video.videoHeight));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(video.videoWidth * scale);
        canvas.height = Math.round(video.videoHeight * scale);
        const ctx = canvas.getContext('2d');
        if (!ctx) { finish(null); return; }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        finish(canvas.toDataURL('image/jpeg', 0.85));
      } catch {
        finish(null);
      }
    };
    const onError = () => finish(null);
    video.addEventListener('loadeddata', onReady);
    video.addEventListener('error', onError);
    video.src = src;
    video.load();
  });
}

/* ============ 主组件 ============ */

export function UploadWorkModal({ open, onClose, assetHistory = [], workflowSnapshot }: UploadWorkModalProps) {
  const publishWork = useWorksStore((s) => s.publishWork);
  const publishToServer = useWorksStore((s) => s.publishToServer);
  const addLibraryAssets = useWorksStore((s) => s.addLibraryAssets);
  const storeLibrary = useWorksStore((s) => s.libraryAssets);

  /* —— tab 与通用表单状态 —— */
  const [tab, setTab] = useState<'image' | 'video'>('image');
  const [isPublic, setIsPublic] = useState(true);
  const [publishing, setPublishing] = useState(false);

  /* —— 图片作品状态 —— */
  const [images, setImages] = useState<ImageItem[]>([]);
  const [coverIndex, setCoverIndex] = useState(0);
  const [viewIndex, setViewIndex] = useState(0);
  const [imgTitle, setImgTitle] = useState('');
  const [imgCats, setImgCats] = useState<string[]>([]);
  const [catDropdownOpen, setCatDropdownOpen] = useState(false);
  const [imgPrompt, setImgPrompt] = useState('');

  /* —— 视频作品状态（videoFrame=自动抓取的首帧，作默认封面显示） —— */
  const [videoSrc, setVideoSrc] = useState<string | null>(null);
  const [videoCover, setVideoCover] = useState<string | null>(null);
  const [videoFrame, setVideoFrame] = useState<string | null>(null);
  const [vidTitle, setVidTitle] = useState('');
  const [vidCats, setVidCats] = useState<string[]>([]);
  const [vidDesc, setVidDesc] = useState('');

  /* —— 双形式浮层：触发目标（本地上传 / 历史图库）+ 鼠标按下点（面板坐标系） —— */
  const [uploadTarget, setUploadTarget] = useState<UploadTarget>(null);
  const [uploadAnchor, setUploadAnchor] = useState<{ x: number; y: number } | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  /* —— 文件选择 refs（本地上传） —— */
  const imgInputRef = useRef<HTMLInputElement>(null);
  const vidInputRef = useRef<HTMLInputElement>(null);
  const vidCoverInputRef = useRef<HTMLInputElement>(null);


  /* —— 历史图库数据源：画布 assetHistory ∪ store.libraryAssets（id 去重） —— */
  const historyAssets = useMemo<LibraryAsset[]>(() => {
    const merged = new Map<string, LibraryAsset>();
    assetHistory
      .filter((a: any) => a && a.src && (a.type === 'image' || a.type === 'video' || a.type === 'audio'))
      .forEach((a: any) => merged.set(String(a.id), {
        id: String(a.id),
        type: a.type,
        src: a.src,
        title: a.title || a.name || '未命名资产',
        timestamp: a.timestamp || Date.now(),
        prompt: typeof a.prompt === 'string' && a.prompt.trim() ? a.prompt.trim() : undefined,
      }));
    storeLibrary.forEach((a) => { if (!merged.has(a.id)) merged.set(a.id, a); });
    return Array.from(merged.values());
  }, [assetHistory, storeLibrary]);

  /* —— 弹窗关闭时重置全部状态 —— */
  useEffect(() => {
    if (open) return;
    setTab('image'); setIsPublic(false); setPublishing(false);
    setImages([]); setCoverIndex(0); setViewIndex(0);
    setImgTitle(''); setImgCats([]); setCatDropdownOpen(false); setImgPrompt('');
    setVideoSrc(null); setVideoCover(null); setVideoFrame(null);
    setVidTitle(''); setVidCats([]); setVidDesc('');
    setUploadTarget(null); setHistoryOpen(false); setUploadAnchor(null);
  }, [open]);

  /* —— Esc 关闭 —— */
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);


  const handleClose = useCallback(() => {
    setUploadTarget(null); setHistoryOpen(false); setUploadAnchor(null);
    onClose();
  }, [onClose]);

  /* —— 浮层触发：记录鼠标按下点，映射到面板坐标系（面板无缩放、1:1 CSS 像素；宽度 clamp 后 rect.width 即实际宽） —— */
  const triggerUpload = useCallback((e: React.MouseEvent, target: UploadTarget) => {
    const panel = panelRef.current;
    if (panel) {
      const rect = panel.getBoundingClientRect();
      setUploadAnchor({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    } else {
      setUploadAnchor({ x: PANEL_W - 200, y: PANEL_H - 120 });
    }
    setUploadTarget(target);
  }, []);

  /* —— 本地上传：按触发目标分流（视频封面 → 图片选择器，其余按 tab 走图片/视频选择器） —— */
  const handleLocalUpload = useCallback(() => {
    const target = uploadTarget;
    setUploadTarget(null); setUploadAnchor(null);
    if (target === 'videoCover') vidCoverInputRef.current?.click();
    else if (tab === 'image') imgInputRef.current?.click();
    else vidInputRef.current?.click();
  }, [tab, uploadTarget]);

  /** 图片 tab：批量选择本地图片（最多补足 8 张） */
  const onPickImages = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const room = MAX_IMAGES - images.length;
    const picked = files.slice(0, Math.max(0, room));
    const uploaded: ImageItem[] = [];
    for (const f of picked) {
      const url = await uploadToTemp(f, 'works');
      if (url) uploaded.push({ id: genId(), src: url });
    }
    if (uploaded.length) {
      setImages((prev) => {
        const next = [...prev, ...uploaded];
        setCoverIndex((ci) => Math.min(ci, next.length - 1));
        setViewIndex(next.length - 1);
        return next;
      });
    }
  }, [images.length]);

  /** 视频 tab：选择本地视频文件（成功后自动抓取首帧作默认封面） */
  const onPickVideo = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = (e.target.files || [])[0];
    e.target.value = '';
    if (!file) return;
    const url = await uploadToTemp(file, 'works');
    if (url) {
      setVideoSrc(url);
      setVideoCover(null);
      setVideoFrame(null);
      const frame = await grabVideoFirstFrame(url);
      if (frame) setVideoFrame(frame);
    }
  }, []);

  /** 视频 tab：选择本地封面图 */
  const onPickVideoCover = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = (e.target.files || [])[0];
    e.target.value = '';
    if (!file) return;
    const url = await uploadToTemp(file, 'works');
    if (url) setVideoCover(url);
  }, []);

  /** 移除一张已上传图片 */
  const removeImage = useCallback((id: string) => {
    setImages((prev) => {
      const idx = prev.findIndex((im) => im.id === id);
      const next = prev.filter((im) => im.id !== id);
      setCoverIndex((ci) => Math.max(0, Math.min(ci > idx ? ci - 1 : ci, next.length - 1)));
      setViewIndex((vi) => Math.max(0, Math.min(vi > idx ? vi - 1 : vi, next.length - 1)));
      return next;
    });
  }, []);

  /** 类目多选（最多 2 个） */
  const toggleCat = useCallback((cat: string, current: string[], setter: (v: string[]) => void) => {
    setter(current.includes(cat) ? current.filter((c) => c !== cat) : current.length >= 2 ? current : [...current, cat]);
  }, []);


  /* —— 历史图库确认：按触发目标回填 —— */
  const handleHistoryConfirm = useCallback((picked: LibraryAsset[]) => {
    setHistoryOpen(false);
    if (!picked.length) return;
    if (uploadTarget === 'video') {
      const v = picked.find((p) => p.type === 'video');
      if (v) {
        setVideoSrc(v.src);
        setVideoCover(null);
        setVideoFrame(null);
        grabVideoFirstFrame(v.src).then((f) => { if (f) setVideoFrame(f); });
        // 生成提示词回填提示词栏
        const p = v.prompt?.trim();
        if (p) setVidDesc(p.slice(0, MAX_PROMPT));
      }
    } else if (uploadTarget === 'videoCover') {
      const c = picked.find((p) => p.type === 'image');
      if (c) setVideoCover(c.src);
    } else {
      const imgs = picked.filter((p) => p.type === 'image');
      if (imgs.length) {
        // 生成提示词回填提示词栏（取首个选中资产）
        const p = imgs[0]?.prompt?.trim();
        if (p) setImgPrompt(p.slice(0, MAX_PROMPT));
        setImages((prev) => {
          const next = [...prev, ...imgs.map((p) => ({ id: p.id + '-' + genId(), src: p.src }))].slice(0, MAX_IMAGES);
          setCoverIndex((ci) => Math.min(ci, next.length - 1));
          setViewIndex(next.length - 1);
          return next;
        });
      }
    }
    setUploadTarget(null); setUploadAnchor(null);
  }, [uploadTarget]);

  /* —— 发布校验 —— */
  const canPublish = tab === 'image'
    ? images.length > 0 && imgTitle.trim().length > 0
    : !!videoSrc && vidTitle.trim().length > 0;

  /* —— 发布：优先落库（POST /api/works，作者按登录用户生成），失败兜底本地发布；
        并归档本次上传资产（addLibraryAssets） —— */
  const handlePublish = useCallback(async () => {
    if (!canPublish || publishing) return;
    setPublishing(true);
    try {
      // 公开制作流程：发布瞬间抓取源项目画布快照（全部节点/连线/分组），
      // 「查看制作流程」与「复制项目」以该快照为准（与实际节点一致）；未公开或空画布不写入
      const workflowData = (() => {
        if (!isPublic) return undefined;
        const snap = workflowSnapshot?.();
        return snap && snap.nodes.length > 0 ? snap : undefined;
      })();
      if (tab === 'image') {
        // 封面自然尺寸随作品存档：Bong作品页瀑布流直接按宽高比排布，避免首帧重排
        const coverSrc = images[coverIndex]?.src || images[0].src;
        const coverSize = await new Promise<{ w: number; h: number } | undefined>((resolve) => {
          const img = new Image();
          img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
          img.onerror = () => resolve(undefined);
          img.src = coverSrc;
        });
        const payload = {
          type: 'image' as const,
          url: coverSrc,
          coverWidth: coverSize?.w,
          coverHeight: coverSize?.h,
          images: images.map((im) => im.src),
          title: imgTitle.trim(),
          categories: imgCats,
          prompt: imgPrompt.trim(),
          isPublicWorkflow: isPublic,
          workflowData,
        };
        // 落库成功后 store 内已前插服务端记录；失败（未登录/网络异常）兜底本地发布，
        // 待下次 hydrateFromServer 时自动迁移
        const done = await publishToServer(payload);
        if (!done) {
          publishWork({
            id: genId(),
            ...payload,
            likes: 0,
            createdAt: Date.now(),
            authorName: '我',
          });
        }
        addLibraryAssets(images.map((im) => ({
          id: im.id, type: 'image' as const, src: im.src,
          title: imgTitle.trim() || '图片作品', timestamp: Date.now(),
          prompt: imgPrompt.trim() || undefined,
        })));
      } else if (videoSrc) {
        const payload = {
          type: 'video' as const,
          url: videoSrc,
          /* 封面三级兜底：手动上传封面 → 自动抓取首帧（选片时已生成）→ 无 */
          coverUrl: videoCover || videoFrame || undefined,
          title: vidTitle.trim(),
          categories: vidCats,
          prompt: vidDesc.trim(),
          isPublicWorkflow: isPublic,
          workflowData,
        };
        const done = await publishToServer(payload);
        if (!done) {
          publishWork({
            id: genId(),
            ...payload,
            likes: 0,
            createdAt: Date.now(),
            authorName: '我',
          });
        }
        addLibraryAssets([{
          id: genId(), type: 'video' as const, src: videoSrc,
          title: vidTitle.trim() || '视频作品', timestamp: Date.now(),
          prompt: vidDesc.trim() || undefined,
        }]);
      }
      handleClose();
    } finally {
      setPublishing(false);
    }
  }, [tab, images, coverIndex, imgTitle, imgCats, imgPrompt, videoSrc, videoCover,
      vidTitle, vidCats, vidDesc, isPublic, canPublish, publishing,
      publishWork, publishToServer, addLibraryAssets, workflowSnapshot, handleClose]);


  if (!open) return null;

  const isImgTab = tab === 'image';

  return (
    <div className="fixed inset-0" style={{ zIndex: 1000 }}>
      {/* 遮罩：#121212 blur(8px) opacity 0.65（设计稿 235:466） */}
      <div
        className="absolute inset-0 bg-[#121212]/65 backdrop-blur-[8px]"
        onClick={handleClose}
      />
      {/* 主面板（流式自适应：居中定位 + 尺寸加视口上限——宽窗（≥832px）下与原 800×700 完全一致，
          窄窗/矮窗时收缩到视口内（四周边距 32px），字号恒定、内部弹性区与滚动区消化空间；缩放体系已退役） */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div
          ref={panelRef}
          className="pointer-events-auto relative w-[min(800px,100vw-32px)] h-[min(700px,100vh-32px)] bg-[#1D1D1D] rounded-2xl border border-[#2D2D2D] shadow-2xl flex flex-col overflow-visible"
          onClick={(e) => e.stopPropagation()}
        >
          {/* 隐藏的本地文件选择器 */}
          <input ref={imgInputRef} type="file" accept="image/*" multiple hidden onChange={onPickImages} />
          <input ref={vidInputRef} type="file" accept="video/*" hidden onChange={onPickVideo} />
          <input ref={vidCoverInputRef} type="file" accept="image/*" hidden onChange={onPickVideoCover} />

          {/* —— 头部：标题（设计稿 @28,32）+ 公开制作流程 toggle（设计稿 @648,80；文字 14px Medium 白） —— */}
          <div className="relative flex items-center px-7 pt-8">
            <div className="text-[16px] font-semibold text-white/45 leading-none">发布作品到 Bong AI</div>
            {/* 右上角关闭按钮（与标题行垂直居中对齐） */}
            <button
              type="button"
              aria-label="关闭"
              title="关闭"
              className="absolute right-7 top-[26px] z-10 w-7 h-7 flex items-center justify-center text-white/50 hover:text-white transition-colors"
              onClick={handleClose}
            >
              <X size={20} strokeWidth={1.5} />
            </button>
            <button
              type="button"
              className="absolute right-7 top-[80px] z-10 flex items-center gap-2 group"
              onClick={() => setIsPublic((v) => !v)}
            >
              {/* 32x16 滑块（设计稿 235:573）：同意（默认）= 白轨道 + 深灰点居右；点击后圆点滑到左侧，轨道白→灰、圆点灰→白 */}
              <span
                className="relative w-8 h-4 shrink-0 rounded-full transition-colors duration-200"
                style={{ backgroundColor: isPublic ? '#FFFFFF' : '#4D4D4D' }}
              >
                <span
                  className="absolute top-[2px] w-3 h-3 rounded-full transition-[left,background-color] duration-200 ease-out"
                  style={{ left: isPublic ? 18 : 2, backgroundColor: isPublic ? '#1D1D1D' : '#FFFFFF' }}
                />
              </span>
              <span className="text-[14px] font-medium leading-none text-white group-hover:text-white/70 transition-colors">
                公开制作流程
              </span>
            </button>
          </div>


          {/* —— 双 Tab + 下划线 + 全宽分割线（设计稿 y=81/111） —— */}
          <div className="px-7 pt-[33px]">
            <div className="flex items-center gap-[24px]">
              {(['image', 'video'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  className="relative pb-4 text-[14px] font-medium leading-none transition-colors"
                  style={{ color: tab === t ? '#FFFFFF' : 'rgba(255,255,255,0.45)' }}
                  onClick={() => setTab(t)}
                >
                  {t === 'image' ? '图片作品' : '视频作品'}
                  {tab === t && <span className="absolute left-0 right-0 -bottom-px h-px bg-white" />}
                </button>
              ))}
            </div>
          </div>
          <div className="mx-6 h-px bg-white/15" />

          {/* —— 滚动兜底容器（模式同图像作品预览页右栏）：高窗口主体区撑满、底部参数与发布按钮贴底固定不滚；
              矮窗口整体纵向滚动（左侧上传区、右侧表单、底部参数与按钮一起滚动浏览），字号恒定 —— */}
          <div className="flex min-h-0 w-full flex-1 flex-col overflow-y-auto no-scrollbar">
            {/* —— 主体：左侧媒体区 260 宽 + 右侧表单 477 宽（min-h = 容器可视高 − 底部栏 80：
                高窗口恰好撑满贴底；矮窗口被表单固有内容撑高，触发外层滚动） —— */}
            <div className="flex shrink-0 min-h-[calc(100%-80px)] px-6 pt-4 pb-8 gap-[15px]">
            {/* ============ 左侧：图片 tab ============ */}
            {isImgTab && (
              images.length === 0 ? (
                /* 空态上传区 260 宽（设计稿 260x460；高度弹性：默认拉满主体区 ~460，矮窗时跟随收缩） */
                <button
                  type="button"
                  className="relative w-[260px] shrink-0 self-stretch max-h-[460px] rounded-xl bg-[#2D2D2D] flex flex-col items-center justify-center gap-3 hover:bg-[#333333] transition-colors"
                  onClick={(e) => triggerUpload(e, 'image')}
                >
                  <span className="w-9 h-9 rounded-lg border border-dashed border-[#4D4D4D] bg-[#353535] flex items-center justify-center">
                    <img src="/icons/upload-work/icon-upload.svg" alt="" className="w-4 h-4" />
                  </span>
                  <span className="text-[14px] font-medium text-[#999999]">上传作品</span>
                </button>
              ) : (
                <div className="w-[260px] shrink-0 flex flex-col min-h-0">
                  {/* 大图预览 260 宽 + 页码/设为封面胶囊 + 翻页箭头（设计稿 260x346；高度弹性 flex-1：
                      默认与原固定 346 完全一致，矮窗时先于缩略图收缩、保底 160 保证图片可见） */}
                  <div className="relative w-[260px] flex-1 min-h-[160px] rounded-xl bg-[#2D2D2D] overflow-hidden">
                    <img src={images[viewIndex]?.src} alt="" className="w-full h-full object-contain" />
                    {/* 页码胶囊 44x24「2 / 8」（设计稿 left-2 bottom-2，12px Medium） */}
                    <span className="absolute left-2 bottom-2 h-6 min-w-[44px] px-2 rounded-full flex items-center justify-center text-[12px] font-medium text-white bg-[rgba(13,13,13,0.45)]">
                      {viewIndex + 1} / {images.length}
                    </span>
                    {/* 设为封面胶囊 78x24 圆角8（白底黑勾 icon 在左 + 文字，设计稿右下） */}
                    <button
                      type="button"
                      className="absolute right-2 bottom-2 h-6 pl-1 pr-1.5 rounded-lg flex items-center gap-1 text-[12px] text-white bg-[rgba(13,13,13,0.65)] hover:bg-[rgba(13,13,13,0.85)] transition-colors"
                      onClick={() => setCoverIndex(viewIndex)}
                    >
                      {coverIndex !== viewIndex && <img src="/icons/upload-work/icon-check.svg" alt="" className="w-4 h-4 shrink-0" />}
                      {coverIndex === viewIndex ? '当前封面' : '设为封面'}
                    </button>
                    {/* 翻页箭头 36x36 自带 #0D0D0D/0.45 圆角12 底（设计稿 @8,155 / @216,155 垂直居中） */}
                    {viewIndex > 0 && (
                      <button type="button" className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center"
                        onClick={() => setViewIndex((v) => Math.max(0, v - 1))}>
                        <img src="/icons/upload-work/icon-arrow-left.svg" alt="" className="w-9 h-9" />
                      </button>
                    )}
                    {viewIndex < images.length - 1 && (
                      <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center"
                        onClick={() => setViewIndex((v) => Math.min(images.length - 1, v + 1))}>
                        <img src="/icons/upload-work/icon-arrow-right.svg" alt="" className="w-9 h-9" />
                      </button>
                    )}
                  </div>


                  {/* 缩略图 48x48 五列（列距5 行距6；选中=1px 白描边+内图42x42@3,3，常态=无描边+内图44x44@2,2+暗遮罩；删除钮 16x16 右上内缩4px） */}
                  <div className="mt-3 grid grid-cols-5 gap-x-[5px] gap-y-[6px]">
                    {images.map((im, i) => {
                      const selected = i === viewIndex;
                      return (
                        <div
                          key={im.id}
                          className={`relative w-12 h-12 rounded-lg cursor-pointer ${selected ? 'border border-white' : 'border-[0.5px] border-white/0'}`}
                          onClick={() => setViewIndex(i)}
                        >
                          <img
                            src={im.src}
                            alt=""
                            className={`absolute object-cover rounded-md ${selected ? 'left-[3px] top-[3px] w-[42px] h-[42px]' : 'left-[2px] top-[2px] w-[44px] h-[44px]'}`}
                          />
                          {!selected && (
                            <span className="absolute left-[2px] top-[2px] w-[44px] h-[44px] rounded-md bg-[rgba(13,13,13,0.25)] pointer-events-none" />
                          )}
                          <button
                            type="button"
                            className="absolute top-1 right-1 w-4 h-4 hover:opacity-80 transition-opacity"
                            onClick={(e) => { e.stopPropagation(); removeImage(im.id); }}
                          >
                            <img src="/icons/upload-work/icon-thumb-delete.svg" alt="" className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                    {images.length < MAX_IMAGES && (
                      <button
                        type="button"
                        className="w-12 h-12 rounded-lg border border-dashed border-[#4D4D4D] flex items-center justify-center opacity-70 hover:opacity-100 hover:border-[#666666] transition-all"
                        onClick={(e) => triggerUpload(e, 'image')}
                      >
                        <img src="/icons/upload-work/icon-upload.svg" alt="" className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              )
            )}


            {/* ============ 左侧：视频 tab ============ */}
            {!isImgTab && (
              !videoSrc ? (
                <button
                  type="button"
                  className="w-[260px] shrink-0 self-stretch max-h-[460px] rounded-xl bg-[#2D2D2D] flex flex-col items-center justify-center gap-3 hover:bg-[#333333] transition-colors"
                  onClick={(e) => triggerUpload(e, 'video')}
                >
                  <span className="w-9 h-9 rounded-lg border border-dashed border-[#4D4D4D] bg-[#353535] flex items-center justify-center">
                    <img src="/icons/upload-work/icon-upload.svg" alt="" className="w-4 h-4" />
                  </span>
                  <span className="text-[14px] font-medium text-[#999999]">上传作品</span>
                </button>
              ) : (
                <div className="w-[260px] shrink-0 self-stretch max-h-[460px] rounded-xl bg-[#2D2D2D] overflow-hidden relative">
                  <video src={videoSrc} className="w-full h-full object-contain" controls preload="metadata" />
                  <button
                    type="button"
                    className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/60 flex items-center justify-center text-white/80 hover:text-white transition-colors"
                    onClick={() => { setVideoSrc(null); setVideoCover(null); setVideoFrame(null); }}
                  >
                    <Trash2 size={12} strokeWidth={1.5} />
                  </button>
                </div>
              )
            )}


            {/* ============ 右侧表单 477 宽（矮窗口由外层滚动容器整体兜底，本列不单独滚动） ============ */}
            <div className="flex-1 min-w-0 flex flex-col relative pt-2">
              {isImgTab ? (
                <>
                  {/* 作品标题（设计稿 @306,136 → 输入框 @299,162 477x48） */}
                  <label className="flex items-center gap-1 pl-[7px] text-[14px] font-medium text-white leading-none">
                    作品标题
                    <span className="w-2 h-2 rounded-[0.5px] bg-[#CC001B]" />
                  </label>
                  <input
                    value={imgTitle}
                    maxLength={MAX_TITLE}
                    onChange={(e) => setImgTitle(e.target.value)}
                    placeholder="输入作品标题，不超过40个汉字"
                    className="mt-[10px] w-full h-12 rounded-xl bg-[#2D2D2D] px-4 text-[14px] font-medium text-white placeholder:text-[#999999] outline-none focus:ring-1 focus:ring-white/30 transition-shadow"
                  />

                  {/* 作品类目（多选，最多 2 个；设计稿 @299,252 477x48） */}
                  <label className="mt-4 flex items-center gap-1 pl-[7px] text-[14px] font-medium text-white leading-none">
                    作品类目
                    <span className="w-2 h-2 rounded-[0.5px] bg-[#CC001B]" />
                  </label>
                  <div className="relative mt-[10px]">
                    <button
                      type="button"
                      className={`w-full h-12 rounded-xl bg-[#2D2D2D] flex items-center justify-between hover:ring-1 hover:ring-white/20 transition-shadow ${imgCats.length > 0 ? 'p-2' : 'px-4'}`}
                      onClick={() => setCatDropdownOpen((v) => !v)}
                    >
                      {imgCats.length > 0 ? (
                        <span className="flex items-center gap-2 flex-wrap">
                          {imgCats.map((c) => (
                            <span key={c} className="w-20 h-8 rounded-lg bg-[#3D3D3D] border border-[#4D4D4D] flex items-center justify-center text-[14px] text-white shrink-0">
                              {c}
                            </span>
                          ))}
                        </span>
                      ) : (
                        <span className="text-[14px] text-[#999999]">请选择作品类目，最多可选2个</span>
                      )}
                      <ChevronDown size={16} strokeWidth={1.5} className="text-[#8A8A8A] shrink-0" />
                    </button>
                    {catDropdownOpen && (
                      <>
                        <div className="fixed inset-0" onClick={() => setCatDropdownOpen(false)} />
                        <div className="absolute left-0 right-0 top-[52px] z-20 rounded-xl bg-[#252525] border border-[#3D3D3D] shadow-2xl p-2 grid grid-cols-4 gap-1.5">
                          {CATEGORIES.map((c) => {
                            const on = imgCats.includes(c);
                            return (
                              <button
                                key={c}
                                type="button"
                                className="h-8 rounded-lg text-[12px] transition-colors"
                                style={{
                                  backgroundColor: on ? '#3D3D3D' : 'transparent',
                                  border: `1px solid ${on ? '#4D4D4D' : 'transparent'}`,
                                  color: on ? '#FFFFFF' : 'rgba(255,255,255,0.6)',
                                }}
                                onClick={() => toggleCat(c, imgCats, setImgCats)}
                              >
                                {c}
                              </button>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>


                  {/* 提示词（设计稿 @299,342 477x246 + 计数 0/500） */}
                  <label className="mt-4 flex items-center gap-1 pl-[7px] text-[14px] font-medium text-white leading-none">
                    作品提示词
                    <span className="w-2 h-2 rounded-[0.5px] bg-[#CC001B]" />
                  </label>
                  <div className="relative mt-[10px] flex-1 min-h-0">
                    <textarea
                      value={imgPrompt}
                      maxLength={MAX_PROMPT}
                      onChange={(e) => setImgPrompt(e.target.value)}
                      placeholder="请输入作品的图片提示词"
                      className="w-full h-full min-h-[120px] rounded-xl bg-[#2D2D2D] p-4 text-[14px] leading-6 text-white placeholder:text-[#999999] outline-none focus:ring-1 focus:ring-white/30 resize-none transition-shadow"
                    />
                    <span className="absolute right-4 bottom-4 text-[12px] text-[#999999]">
                      {imgPrompt.length} / {MAX_PROMPT}
                    </span>
                  </div>
                </>
              ) : (


                <>
                  {/* 视频封面（设计稿 235:148 封面上传条 477x80：64x64 虚线框 icon_TP + 14px 文字 + 右侧 36x36 #4D4D4D 圆角12 按钮） */}
                  <label className="flex items-center gap-1 pl-[7px] text-[14px] font-medium text-white leading-none">视频封面</label>
                  <div className="mt-[10px] w-full h-20 rounded-xl bg-[#2D2D2D] relative flex items-center pl-2 pr-3">
                    {(videoCover || videoFrame) ? (
                      <>
                        <button type="button" className="w-16 h-16 rounded-lg overflow-hidden shrink-0" onClick={(e) => triggerUpload(e, 'videoCover')}>
                          <img src={videoCover || videoFrame || ''} alt="" className="w-16 h-16 object-cover" />
                        </button>
                        <span className="ml-4 text-[14px] text-[#999999] flex-1 min-w-0 truncate">
                          {videoCover ? '已设置自定义封面' : '已抓取视频首帧，可上传自定义封面'}
                        </span>
                        <button
                          type="button"
                          className="w-9 h-9 rounded-xl bg-[#4D4D4D] flex items-center justify-center text-white/80 hover:text-white hover:bg-[#5A5A5A] transition-colors shrink-0"
                          onClick={() => { setVideoCover(null); setVideoFrame(null); }}
                        >
                          <Trash2 size={16} strokeWidth={1.5} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="flex items-center gap-4 text-left flex-1 min-w-0"
                          onClick={(e) => triggerUpload(e, 'videoCover')}
                        >
                          <span className="w-16 h-16 rounded-lg border border-dashed border-[#4D4D4D] bg-[#353535] flex items-center justify-center shrink-0">
                            <img src="/icons/upload-work/icon-image.svg" alt="" className="w-4 h-4" />
                          </span>
                          <span className="text-[14px] text-[#999999]">默认使用视频首帧，并支持上传</span>
                        </button>
                        <button
                          type="button"
                          className="w-9 h-9 rounded-xl bg-[#4D4D4D] flex items-center justify-center hover:bg-[#5A5A5A] transition-colors shrink-0"
                          onClick={(e) => triggerUpload(e, 'videoCover')}
                        >
                          <img src="/icons/upload-work/icon-cover-btn.svg" alt="" className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>

                  {/* 视频标题（设计稿 @299,284 477x48） */}
                  <label className="mt-4 flex items-center gap-1 pl-[7px] text-[14px] font-medium text-white leading-none">
                    视频标题
                    <span className="w-2 h-2 rounded-[0.5px] bg-[#CC001B]" />
                  </label>
                  <input
                    value={vidTitle}
                    maxLength={MAX_TITLE}
                    onChange={(e) => setVidTitle(e.target.value)}
                    placeholder="输入作品标题，不超过40个汉字"
                    className="mt-[10px] w-full h-12 rounded-xl bg-[#2D2D2D] px-4 text-[14px] font-medium text-white placeholder:text-[#999999] outline-none focus:ring-1 focus:ring-white/30 transition-shadow"
                  />


                  {/* 视频类目（设计稿 @299,374 477x48） */}
                  <label className="mt-4 flex items-center gap-1 pl-[7px] text-[14px] font-medium text-white leading-none">
                    视频类目
                    <span className="w-2 h-2 rounded-[0.5px] bg-[#CC001B]" />
                  </label>
                  <div className="relative mt-[10px]">
                    <button
                      type="button"
                      className={`w-full h-12 rounded-xl bg-[#2D2D2D] flex items-center justify-between hover:ring-1 hover:ring-white/20 transition-shadow ${vidCats.length > 0 ? 'p-2' : 'px-4'}`}
                      onClick={() => setCatDropdownOpen((v) => !v)}
                    >
                      {vidCats.length > 0 ? (
                        <span className="flex items-center gap-2">
                          {vidCats.map((c) => (
                            <span key={c} className="w-20 h-8 rounded-lg bg-[#3D3D3D] border border-[#4D4D4D] flex items-center justify-center text-[14px] text-white shrink-0">{c}</span>
                          ))}
                        </span>
                      ) : (
                        <span className="text-[14px] text-[#999999]">请选择作品类目，最多可选2个</span>
                      )}
                      <ChevronDown size={16} strokeWidth={1.5} className="text-[#8A8A8A] shrink-0" />
                    </button>
                    {catDropdownOpen && (
                      <>
                        <div className="fixed inset-0" onClick={() => setCatDropdownOpen(false)} />
                        <div className="absolute left-0 right-0 top-[52px] z-20 rounded-xl bg-[#252525] border border-[#3D3D3D] shadow-2xl p-2 grid grid-cols-4 gap-1.5">
                          {CATEGORIES.map((c) => {
                            const on = vidCats.includes(c);
                            return (
                              <button key={c} type="button" className="h-8 rounded-lg text-[12px] transition-colors"
                                style={{ backgroundColor: on ? '#3D3D3D' : 'transparent', border: `1px solid ${on ? '#4D4D4D' : 'transparent'}`, color: on ? '#FFFFFF' : 'rgba(255,255,255,0.6)' }}
                                onClick={() => toggleCat(c, vidCats, setVidCats)}>
                                {c}
                              </button>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>


                  {/* 视频描述（设计稿 @299,464 477x124 固定高） */}
                  <label className="mt-4 flex items-center gap-1 pl-[7px] text-[14px] font-medium text-white leading-none">视频描述</label>
                  <textarea
                    value={vidDesc}
                    maxLength={MAX_PROMPT}
                    onChange={(e) => setVidDesc(e.target.value)}
                    placeholder="请描述一下作品或创作思路"
                    className="mt-[10px] w-full h-[124px] rounded-xl bg-[#2D2D2D] p-4 text-[14px] leading-6 text-white placeholder:text-[#999999] outline-none focus:ring-1 focus:ring-white/30 resize-none transition-shadow"
                  />
                </>
              )}
            </div>
          </div>


          {/* —— 底部：参数信息行（设计稿 @28,636，仅图片 tab）+ 发布按钮（@576,620 200x48，禁用 opacity 0.25）；
              位于滚动容器内：高窗口贴底固定，矮窗口随整体滚动 —— */}
          <div className="relative h-[80px] shrink-0 flex items-start justify-end pr-6">
            {isImgTab && (
              <div className="absolute left-7 top-4 flex items-center text-[12px] font-light text-[#8A8A8A] tracking-[0.04em] select-none">
                <span className="mr-2">参数信息：</span>
                <img src="/icons/upload-work/model-bong-image.png" alt="" className="w-4 h-4 rounded opacity-65 object-cover" />
                <span className="ml-2">智能参考</span>
                <span className="mx-3 w-px h-2 bg-white/15" />
                <span>Bong Image 2.0</span>
                <span className="mx-3 w-px h-2 bg-white/15" />
                <span>9:16</span>
                <span className="mx-3 w-px h-2 bg-white/15" />
                <span>2K</span>
              </div>
            )}
            <button
              type="button"
              disabled={!canPublish || publishing}
              onClick={handlePublish}
              className="w-[200px] h-12 rounded-xl bg-white text-[#121212] text-[16px] font-medium transition-opacity disabled:opacity-25 disabled:cursor-not-allowed hover:opacity-90"
            >
              {publishing ? '发布中…' : '发布'}
            </button>
          </div>
          </div>

          {/* 双形式浮层：本地上传 / 历史图库（挂在主面板直接子级，跟随鼠标按下点弹出，靠近面板边缘时自动内收） */}
          {uploadTarget && uploadAnchor && (
            <>
              <div className="fixed inset-0" onClick={() => setUploadTarget(null)} />
              <div
                className="absolute w-[156px] rounded-xl bg-[#181818] border border-[#2D2D2D] shadow-2xl p-1 z-30"
                style={{
                  /* 边界内收改用 CSS clamp + calc(100%)：面板宽度 clamp 后仍按实际尺寸收边 */
                  left: `clamp(8px, ${uploadAnchor.x}px, calc(100% - 172px))`,
                  top: `clamp(8px, ${uploadAnchor.y}px, calc(100% - 100px))`,
                }}
              >
                <button
                  type="button"
                  className="w-full h-10 rounded-lg bg-[#2D2D2D] hover:bg-[#383838] text-[14px] text-white transition-colors"
                  onClick={handleLocalUpload}
                >
                  本地上传
                </button>
                <button
                  type="button"
                  className="w-full h-10 rounded-lg hover:bg-[#2D2D2D] text-[14px] text-white/80 hover:text-white transition-colors mt-1"
                  onClick={() => { setHistoryOpen(true); }}
                >
                  历史图库
                </button>
              </div>
            </>
          )}

          {/* —— 历史图库面板（设计稿 235:226，独立组件，覆盖于本弹窗之上） —— */}
          <HistoryAssetsPanel
            open={historyOpen}
            acceptType={uploadTarget === 'videoCover' ? 'image' : uploadTarget === 'video' ? 'video' : 'image'}
            assets={historyAssets}
            onClose={() => { setHistoryOpen(false); setUploadTarget(null); setUploadAnchor(null); }}
            onConfirm={handleHistoryConfirm}
            onUploadClick={() => { setHistoryOpen(false); handleLocalUpload(); }}
          />
        </div>
      </div>
    </div>
  );
}
